import React, { useState, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  CircularProgress,
  Button,
  Tooltip,
  Stack,
  Menu,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import SettingsIcon from '@mui/icons-material/Settings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useThemeMode } from '../theme/ThemeContext';
import AuroraDeFundo from './AuroraDeFundo';
import ReflexoDeVidro from './ReflexoDeVidro';
import { brilhoDeTopo, CURVAS, TEMPOS } from '../theme/vidro';
import { ABAS } from '../telas/abas';

const drawerWidth = 280;

/**
 * A navegacao das sete telas, na ordem em que o dono le: veredito primeiro,
 * caixa depois, retorno por ultimo. DRE e Balanco ficam ao final porque sao
 * conferencia, nao leitura — quem abre e o consultor com o contador do lado.
 */
const menuItems = [
  { id: ABAS.SAUDE, label: 'Saúde', icon: <MonitorHeartOutlinedIcon />, requiresData: true },
  { id: ABAS.RESULTADO, label: 'Resultado', icon: <PaymentsOutlinedIcon />, requiresData: true },
  { id: ABAS.CAIXA_CICLO, label: 'Caixa e Ciclo', icon: <SavingsOutlinedIcon />, requiresData: true },
  { id: ABAS.DIVIDA_RETORNO, label: 'Dívida e Retorno', icon: <CreditScoreOutlinedIcon />, requiresData: true },
  { id: ABAS.PAINEL, label: 'Painel de Indicadores', icon: <TableChartOutlinedIcon />, requiresData: true },
  { id: ABAS.ANALISE, label: 'Análise do Consultor', icon: <PsychologyOutlinedIcon />, badge: true, requiresData: true },
  { id: ABAS.DRE, label: 'DRE', icon: <ReceiptIcon />, requiresData: true },
  { id: ABAS.BALANCO, label: 'Balanço', icon: <AccountBalanceIcon />, requiresData: true },
  // Planos fica sempre alcançável, inclusive sem dado: ela responde "por que
  // este mês não entrou?" e "por que o parecer não saiu?", e as duas perguntas
  // aparecem antes de existir dashboard. Esconder a aba mata o desejo antes de
  // ele nascer — e código inalcançável já custou uma onda inteira neste produto.
  { id: ABAS.PLANOS, label: 'Planos', icon: <WorkspacePremiumOutlinedIcon />, requiresData: false },
  { id: ABAS.CONFIG, label: 'Configurações', icon: <SettingsIcon />, requiresData: true },
];

const ModernLayout = ({
  children,
  activeTab,
  onTabChange,
  onExport,
  onClearData,
  alertCount = 0,
  onReimport,
  hasData = false,
  exporting = false,
  // Ter dado não é ter o que exportar: o relatório em PDF é montado a partir da
  // análise do consultor. O botão olhava só `hasData`, ficava habilitado sem
  // análise nenhuma e o clique respondia com um erro — controle habilitado que
  // só serve para recusar é um convite ao engano.
  canExport = true,
  disableDataTabs = false,
  showImportTab = false,
  importTabId = 'import',
  importTabLabel = 'Importação',
  showConfigTab = true,
  showFecharMesTab = false,
  authUser,
  onRequestLogin,
  onLogout,
  onSave,
  saving = false,
  canSave = false,
  lastSavedAt,
  // Respiro extra do conteúdo, mesclado sobre o padrão (16/24/32px de xs a
  // md). Existe para a tela que precisa colar na borda no celular — hoje só o
  // preenchimento guiado — sem afrouxar a régua global das outras dez telas.
  sxDoConteudo = {}
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // O menu de transbordo do cabeçalho no celular, onde as ações raras se
  // recolhem para devolver largura ao nome da tela.
  const [menuDeAcoes, setMenuDeAcoes] = useState(null);
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigationItems = useMemo(() => {
    let items = [...menuItems];
    if (!showConfigTab) {
      items = items.filter((item) => item.id !== ABAS.CONFIG);
    }
    // Fechar o mês não exige dado carregado: é justamente por onde o usuário
    // que já tem histórico volta todo mês, sem passar pela importação.
    if (showFecharMesTab) {
      items.unshift({
        id: ABAS.FECHAR_MES,
        label: 'Fechar o mês',
        icon: <EventRepeatIcon />,
        requiresData: false,
      });
    }
    if (showImportTab) {
      items.unshift({
        id: importTabId,
        label: importTabLabel,
        icon: <UploadFileIcon />,
        requiresData: false,
      });
    }
    return items;
  }, [showConfigTab, showImportTab, showFecharMesTab, importTabId, importTabLabel]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item) => {
    onTabChange(item.id);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const formatLastSaved = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) return isoString;
      return date.toLocaleString('pt-BR');
    } catch (error) {
      return isoString;
    }
  };

  const lastSavedLabel = formatLastSaved(lastSavedAt);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* A marca é a única peça com cor cheia da barra: um bloco de vidro
            iluminado por dentro. O filete claro no topo e a sombra curta
            embaixo são os mesmos do cartão — é assim que uma peça de 34px
            pertence ao mesmo sistema de um bloco de 900px. */}
        <Avatar
          variant="rounded"
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.8125rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            borderRadius: 2,
            color: 'primary.contrastText',
            background: (t) => `linear-gradient(155deg, ${t.palette.primary.light}, ${t.palette.primary.dark})`,
            boxShadow: (t) =>
              t.palette.mode === 'dark'
                ? 'inset 0 1px 0 rgba(255,255,255,0.4), 0 6px 18px -8px rgba(106,166,255,0.9)'
                : 'inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 16px -8px rgba(44,74,110,0.7)'
          }}
        >
          CI
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            ConsultorIA
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Consultoria financeira
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Menu Items */}
      <List sx={{ px: 2, py: 2, flex: 1 }}>
        {navigationItems.map((item) => {
          const disabled = disableDataTabs && item.requiresData;
          const isSelected = !disabled && activeTab === item.id;
          const iconColor = disabled ? 'text.disabled' : isSelected ? 'inherit' : 'text.secondary';

          // Sem deslocamento no hover: seis itens saltando a cada passada do
          // mouse é barulho, não feedback.
          return (
            <Box key={item.id} sx={{ position: 'relative' }}>
              {/* A PÍLULA DA TELA ATIVA NÃO PISCA DE UM ITEM PARA O OUTRO: ELA
                  VIAJA. `layoutId` compartilhado faz o framer-motion interpolar
                  a posição entre o item que perdeu e o que ganhou a seleção, e
                  esse deslocamento de 40px é a única coisa na barra que diz
                  "você saiu dali e chegou aqui". Trocar a cor de fundo de dois
                  itens ao mesmo tempo não diz isso — diz que dois itens
                  mudaram de cor.

                  Ela é o FUNDO, e não o item: por isso mora atrás, em position
                  absolute, e o botão fica transparente. */}
              {isSelected && (
                <motion.div
                  layoutId="pilula-da-navegacao"
                  transition={{ type: 'spring', stiffness: 420, damping: 38, mass: 0.7 }}
                  style={{ position: 'absolute', inset: 0, borderRadius: 12, zIndex: 0 }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '12px',
                      bgcolor: 'action.selected',
                      border: '1px solid',
                      borderColor: (t) =>
                        t.palette.mode === 'dark' ? 'rgba(106,166,255,0.28)' : 'rgba(44,74,110,0.18)',
                      boxShadow: (t) =>
                        t.palette.mode === 'dark'
                          ? 'inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 20px -14px rgba(106,166,255,0.9)'
                          : 'inset 0 1px 0 rgba(255,255,255,0.9)'
                    }}
                  />
                </motion.div>
              )}
              <ListItemButton
                disabled={disabled}
                selected={isSelected}
                onClick={() => {
                  if (disabled) return;
                  handleMenuClick(item);
                }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  // 12px, O MESMO DA PÍLULA QUE VIAJA ATRÁS DELE.
                  // Era `borderRadius: 3`, que o tema multiplica por 10 desde
                  // que a forma base virou 10px: o botão ficava com 30px de
                  // canto sobre uma pílula de 12, e a diferença aparecia toda
                  // vez que a seleção passava por baixo.
                  borderRadius: '12px',
                  mb: 0.5,
                  opacity: disabled ? 0.45 : 1,
                  color: disabled ? 'text.disabled' : 'inherit',
                  transition: `color ${TEMPOS.estado}ms ${CURVAS.estado}`,
                  '&.Mui-selected': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'transparent' },
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  },
                  '&:hover': {
                    bgcolor: disabled || isSelected ? 'transparent' : 'action.hover',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: iconColor,
                  }}
                >
                  {item.badge && alertCount > 0 ? (
                    <Badge
                      badgeContent={alertCount}
                      color="error"
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 600 : 500,
                    color: disabled ? 'text.disabled' : isSelected ? 'inherit' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </Box>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: '12px',
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.5)')
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.04em' }}>
            Versão 1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    // `reducedMotion="user"` desliga TODA animação do framer-motion para quem
    // pediu menos movimento no sistema operacional — a transição de página e o
    // pulo dos botões incluídos. Antes, só as duas barras que crescem
    // respeitavam a preferência, e elas eram a menor parte do movimento da tela.
    <MotionConfig reducedMotion="user">
    {/* A aurora é o que o vidro refrata; o reflexo é o que ele faz com o
        ponteiro. Os dois vivem aqui, no casco, e valem para as dez telas. */}
    <AuroraDeFundo />
    <ReflexoDeVidro />
    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          // A BARRA É VIDRO, e o conteúdo passa por baixo dela.
          //
          // Antes era uma cor sólida com 8px de desfoque — o que dá superfície
          // fosca, não vidro. Com 22px e saturação, o que rola por baixo vira
          // cor viva desfocada: é isso que faz a barra parecer estar ACIMA da
          // página, e não colada nela.
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.55 : 0.6),
          backdropFilter: 'blur(22px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(22px) saturate(1.6)',
          '&::after': { ...brilhoDeTopo(theme.palette.mode === 'dark'), left: 0, right: 0 }
        }}
      >
        <Toolbar>
          {/* Botão só de ícone precisa de nome acessível: sem `aria-label` o
              leitor de tela anuncia "botão" e o teclado navega às cegas. */}
          {/* AS AÇÕES DO CABEÇALHO NÃO CRESCEM MAIS AO PASSAR O MOUSE.
              Cinco botões com o mesmo `scale: 1.05` é o gesto genérico de
              template, e ele dispara cinco vezes na mesma barra — diluindo os
              dois movimentos que foram de fato desenhados (a pílula que viaja e
              o número que chega). O estado da chrome agora é o mesmo do vidro:
              a superfície acende, o alvo não muda de tamanho. */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            aria-label={mobileOpen ? 'Fechar o menu' : 'Abrir o menu'}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component="p"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              color: 'text.primary',
              fontWeight: 600,
              // NO CELULAR ESTE TÍTULO NÃO CABE, E MEIO TÍTULO NÃO INFORMA NADA.
              //
              // Medido a 390px, depois de recolher as ações secundárias: "Painel
              // de Indicadores" pede 153px e recebe 86 — a barra mostrava
              // "Painel de In…", e antes disso só "P.". Recolher mais nada
              // sobrou: o que resta na barra é o menu, Salvar e exportar, que
              // são ação.
              //
              // Some, e não fica faltando: desde esta rodada TODA tela tem um
              // <h1> no conteúdo, logo abaixo da barra, com o nome inteiro e
              // legível. Repetir ali em cima pela metade só rouba largura de
              // quem precisa dela.
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {/* O título sai da navegação MONTADA, não da lista estática: as
                abas de importação e de fechar o mês não moram em `menuItems`,
                e o título dizia "Dashboard" para elas.

                Ele é `<p>`, e não um heading: o `<h1>` da tela mora no conteúdo,
                onde cabe inteiro. Dois h1 na mesma página dariam ao leitor de
                tela dois títulos concorrentes para o mesmo lugar. */}
            {navigationItems.find((item) => item.id === activeTab)?.label || 'ConsultorIA'}
          </Typography>

          {/* No celular o título sai, mas a barra não pode colapsar: este vão
              mantém as ações à direita, onde o polegar as procura. */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', sm: 'none' } }} />

          {/* AS AÇÕES SECUNDÁRIAS SAEM DO CAMINHO DO TÍTULO NO CELULAR.

              A 390px, medido: "Painel de Indicadores" tinha 153px de texto e
              23px de espaço — o cabeçalho mostrava "P.". Cinco IconButton mais
              "Salvar" mais o exportar somam mais de 300px de uma viewport de
              390, e o título é o único elemento que encolhe. Tema, limpar e
              reimportar são ações raras: elas vão para um menu de transbordo e
              devolvem a largura para o nome da tela. No desktop nada muda. */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <IconButton
              onClick={toggleTheme}
              aria-label={mode === 'dark' ? 'Usar o tema claro' : 'Usar o tema escuro'}
              sx={{
                color: 'text.primary',
                bgcolor: 'action.hover',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
                mr: 1,
              }}
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>

            {hasData && onClearData && (
              <IconButton
                onClick={onClearData}
                aria-label="Limpar todos os dados importados"
                sx={{ color: 'text.secondary', mr: 0.5 }}
                title="Limpar todos os dados importados"
              >
                <DeleteSweepIcon />
              </IconButton>
            )}

            {onReimport && (
              <IconButton
                onClick={onReimport}
                aria-label="Importar outra planilha"
                title="Importar outra planilha"
                sx={{ color: 'text.secondary', mr: 0.5 }}
              >
                <UploadFileIcon />
              </IconButton>
            )}
          </Box>

          <IconButton
            onClick={(e) => setMenuDeAcoes(e.currentTarget)}
            aria-label="Mais ações"
            aria-haspopup="true"
            aria-expanded={Boolean(menuDeAcoes)}
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: 'text.primary', mr: 0.5 }}
          >
            <MoreVertIcon />
          </IconButton>

          <Menu
            anchorEl={menuDeAcoes}
            open={Boolean(menuDeAcoes)}
            onClose={() => setMenuDeAcoes(null)}
          >
            <MenuItem
              onClick={() => {
                toggleTheme();
                setMenuDeAcoes(null);
              }}
            >
              <ListItemIcon>
                {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{mode === 'dark' ? 'Usar o tema claro' : 'Usar o tema escuro'}</ListItemText>
            </MenuItem>

            {onReimport && (
              <MenuItem
                onClick={() => {
                  onReimport();
                  setMenuDeAcoes(null);
                }}
              >
                <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Importar outra planilha</ListItemText>
              </MenuItem>
            )}

            {hasData && onClearData && (
              <MenuItem
                onClick={() => {
                  onClearData();
                  setMenuDeAcoes(null);
                }}
              >
                <ListItemIcon><DeleteSweepIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Limpar os dados importados</ListItemText>
              </MenuItem>
            )}
          </Menu>

          {onSave && (
            <Tooltip title={authUser ? 'Salvar histórico' : 'Entre para salvar'}>
              <span>
                <Button
                  variant="contained"
                  onClick={onSave}
                  disabled={!canSave || saving}
                  sx={{ mr: 1, px: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {saving ? <CircularProgress size={16} color="inherit" /> : 'Salvar'}
                </Button>
              </span>
            </Tooltip>
          )}

          {lastSavedLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              // No celular o carimbo de salvamento quebrava em três linhas
              // dentro da barra e espremia todos os botões ao lado. Ele é
              // confirmação, não ação: some na largura pequena, onde cada pixel
              // de cabeçalho custa conteúdo.
              sx={{ mr: 2, display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}
            >
              Último salvamento: {lastSavedLabel}
            </Typography>
          )}

          {(() => {
            const exportarBloqueado = !onExport || !hasData || !canExport;
            // Desabilitado tem que dizer POR QUÊ, e a explicação precisa
            // sobreviver ao `disabled`: botão desabilitado não dispara eventos de
            // mouse, então o texto vai num `span` por fora, que é o que o
            // navegador consegue mostrar.
            const explicacao = exporting
              ? 'Gerando o relatório em PDF…'
              : exportarBloqueado
                ? 'O relatório precisa da análise do consultor gerada'
                : 'Exportar o relatório em PDF';
            return (
              <Tooltip title={explicacao}>
                <span>
                  <IconButton
                    onClick={onExport}
                    disabled={exportarBloqueado || exporting}
                    aria-label={exporting ? 'Exportando o relatório' : explicacao}
                    sx={{
                      // DUAS AÇÕES PRIMÁRIAS NA MESMA BARRA NÃO SÃO DUAS
                      // PRIMÁRIAS: são duas concorrentes. "Salvar" já carrega
                      // a cor cheia; o disco azul ao lado dele disputava a
                      // mesma atenção para uma ação que o usuário faz uma vez
                      // por mês. Vira ícone fantasma, como limpar e
                      // reimportar — que é o que ele é.
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        color: 'text.primary',
                        bgcolor: 'action.hover'
                      },
                      mr: 2,
                    }}
                  >
                    {exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            );
          })()}

          {authUser ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, minWidth: 0 }}>
              {/* No celular a barra não comporta o e-mail; ele não é ação, é
                  informação — cede espaço para os controles. */}
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 220 }}
              >
                {authUser.displayName || authUser.email}
              </Typography>
              {/* `color="inherit"` na barra de vidro clara pinta o rótulo de
                  branco sobre fundo branco: medido, "Entrar" e "Sair" saíam
                  ilegíveis no tema claro. Cor de texto é cor de texto. */}
              {onLogout && (
                <Button size="small" onClick={onLogout} sx={{ color: 'text.secondary' }}>
                  Sair
                </Button>
              )}
            </Stack>
          ) : (
            onRequestLogin && (
              <Button size="small" onClick={onRequestLogin} sx={{ ml: 1, color: 'text.primary', fontWeight: 600 }}>
                Entrar
              </Button>
            )
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              backgroundColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(10,12,17,0.86)' : 'rgba(255,255,255,0.86)',
              backdropFilter: 'blur(28px) saturate(1.5)',
              WebkitBackdropFilter: 'blur(28px) saturate(1.5)'
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              borderRight: '1px solid',
              borderColor: 'divider',
              // A barra lateral é a segunda superfície de vidro do produto, e a
              // maior. Ela fica um pouco mais transparente que o cartão porque
              // é chassi, não conteúdo: o que se lê ali são dez palavras.
              backgroundColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.028)' : 'rgba(255,255,255,0.55)',
              backdropFilter: 'blur(26px) saturate(1.5)',
              WebkitBackdropFilter: 'blur(26px) saturate(1.5)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          // `minWidth: 0` NÃO É ENFEITE: É O QUE FAZ O DRE CABER NO CELULAR.
          //
          // Este <main> é item de um container flex. Item de flex nasce com
          // `min-width: auto`, que significa "não encolho abaixo do meu
          // conteúdo". A tabela do DRE com 12 meses mede 1.738px, então o
          // <main> esticava para 1.818px dentro de uma tela de 390 — e o
          // `overflow-x: auto` do TableContainer nunca chegava a valer, porque
          // o container também crescia junto.
          //
          // O efeito no aparelho não era uma barra de rolagem: era o navegador
          // dando zoom-out na PÁGINA INTEIRA para caber 1.818px, deixando todo
          // texto de todas as telas ilegível. E aparecia justamente para quem
          // paga, porque 6 e 12 meses são do plano pago — com 3 meses a tabela
          // cabia e o defeito ficava invisível.
          minWidth: 0
        }}
      >
        <Toolbar />
        {/* A TROCA DE TELA SOBE 10px, NÃO 20.
            20px com 200ms é um salto: o olho vê a peça chegar. 10px com a curva
            de desaceleração longa é o mesmo movimento que o iOS usa para
            empurrar uma tela — percebe-se que algo chegou, não que algo pulou.
            A saída é só opacidade: conteúdo que sai não precisa de trajetória,
            e sair para cima enquanto o novo sobe cria dois movimentos
            concorrentes na mesma dobra. */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: TEMPOS.entrada / 1000, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, ...sxDoConteudo }}>{children}</Box>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
    </MotionConfig>
  );
};

export default ModernLayout;
